import {
    cleanupVideoInput,
    formatFileSize,
    isSupportedVideoFile,
    loadFFmpeg,
    prepareVideoInput,
} from '../compress/compressVideoEngine';

export {
    formatFileSize,
    isSupportedVideoFile,
};

export const MAX_SPEED_VIDEO_BYTES = 200 * 1024 * 1024;
export const MIN_SPEED = 0.25;
export const MAX_SPEED = 4;
export const DEFAULT_SPEED = 1;

export const SPEED_PRESETS = [
    { id: '0.5', value: 0.5 },
    { id: '0.75', value: 0.75 },
    { id: '1', value: 1 },
    { id: '1.25', value: 1.25 },
    { id: '1.5', value: 1.5 },
    { id: '2', value: 2 },
];

export function speedVideoName(fileName, speed = 1) {
    const base = (fileName || 'video').replace(/\.[^.]+$/, '');
    const safeSpeed = Number(speed) || DEFAULT_SPEED;
    const label = Number.isInteger(safeSpeed) ? String(safeSpeed) : safeSpeed.toFixed(2).replace(/\.?0+$/, '');
    return `${base}-${label}x.mp4`;
}

function buildAudioTempoFilter(playbackSpeed) {
    let remaining = playbackSpeed;
    const atempoParts = [];
    while (remaining > 2.0) {
        atempoParts.push('atempo=2.0');
        remaining /= 2.0;
    }
    while (remaining < 0.5) {
        atempoParts.push('atempo=0.5');
        remaining /= 0.5;
    }
    atempoParts.push(`atempo=${remaining.toFixed(6)}`);
    return atempoParts.join(',');
}

/**
 * @param {File} file
 * @param {number} speed
 * @param {(progress: number) => void} onProgress
 * @returns {Promise<Blob>}
 */
export async function changeVideoSpeed(file, speed = DEFAULT_SPEED, onProgress = () => {}) {
    let ffmpeg;
    let videoInput = null;
    const outputName = 'speed-output.mp4';

    try {
        const playbackSpeed = Math.max(MIN_SPEED, Math.min(MAX_SPEED, Number(speed) || DEFAULT_SPEED));
        ffmpeg = await loadFFmpeg();
        videoInput = await prepareVideoInput(ffmpeg, file);

        const progressHandler = ({ progress }) => {
            onProgress(Math.round(Math.min(progress * 100, 100)));
        };
        ffmpeg.on('progress', progressHandler);

        const videoFilter = `setpts=${(1 / playbackSpeed).toFixed(6)}*PTS`;
        const audioFilter = buildAudioTempoFilter(playbackSpeed);

        const exitCode = await ffmpeg.exec([
            '-i', videoInput.inputPath,
            '-filter:v', videoFilter,
            '-filter:a', audioFilter,
            '-vcodec', 'libx264',
            '-preset', 'ultrafast',
            '-crf', '18',
            '-acodec', 'aac',
            '-movflags', '+faststart',
            outputName,
        ]);

        ffmpeg.off('progress', progressHandler);

        if (exitCode !== 0) {
            try { await ffmpeg.deleteFile(outputName); } catch (_) { /* ignore */ }
            throw new Error('ENCODE_FAILED');
        }

        const data = await ffmpeg.readFile(outputName);
        try { await ffmpeg.deleteFile(outputName); } catch (_) { /* ignore */ }

        if (!data || data.length === 0) {
            throw new Error('EMPTY_OUTPUT');
        }

        return new Blob([data], { type: 'video/mp4' });
    } catch (error) {
        const msg = (error?.message || '').toLowerCase();
        if (msg.includes('memory') || msg.includes('allocate') || msg.includes('oom')) {
            throw new Error('OUT_OF_MEMORY');
        }
        if (error?.message === 'ENCODE_FAILED' || error?.message === 'EMPTY_OUTPUT') {
            throw error;
        }
        throw new Error('SPEED_FAILED');
    } finally {
        if (ffmpeg && videoInput) {
            await cleanupVideoInput(ffmpeg, videoInput);
        }
    }
}
